// src/pages/ProProfilePage.js

import React, { useRef, useState, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProfessionalById } from '../api/queries';
import { TabContext } from '../context/TabContext';
import { AuthContext } from '../context/AuthContext';
import ActionButton from '../components/ActionButton';
import './ProProfilePage.css';

import { IoPeopleOutline } from 'react-icons/io5';
import { PiPhone }         from 'react-icons/pi';
import { FaWhatsapp }      from 'react-icons/fa';
import { HiOutlineMail }   from 'react-icons/hi';
import { TfiAlarmClock }   from 'react-icons/tfi';

export default function ProProfilePage() {
  // 🔑 Obtenemos el ID del profesional desde AuthContext
  const { user } = useContext(AuthContext);
  const professionalId = user?.professional_id;
  const { activeTab } = useContext(TabContext);

  // ► React Query: fetch de datos del profesional
  const {
    data: profData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    enabled: !!professionalId,
    staleTime: 5 * 60_000,
    cacheTime: 30 * 60_000
  });

  // Ref y estado para sticky buttons
  const buttonContainerRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  // Sticky logic: al hacer scroll en esta página
  useEffect(() => {
    console.log('[ProProfilePage] useEffect, activeTab =', activeTab);

    if (activeTab !== 'prof_profile') {
      setIsStuck(false);
      return;
    }

    // 1) Detección del target
    const container = document.querySelector('.pro-profile-container');
    const scrollTarget = container || window;
    const isWindow    = !container;

    // 2) Handler unificado
    const handleScroll = () => {
      if (!buttonContainerRef.current) return;

      if (isWindow) {
        // sigue igual para window
        const top = buttonContainerRef.current.getBoundingClientRect().top;
        setIsStuck(top <= -20);
      } else {
        // para contenedor interno: comparamos scrollTop vs offsetTop
        const scrollY = scrollTarget.scrollTop;
        const btnY   = buttonContainerRef.current.offsetTop;
        setIsStuck(scrollY >= btnY - 20);
      }
    };

    // 3) Montaje del listener
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // estado inicial

    // 4) Cleanup
    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab]);

  const scrollToButtons = () => {
    if (buttonContainerRef.current) {
      buttonContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Estados de carga y error
  if (isLoading || !professionalId) {
    return <p style={{ padding: 20 }}>Cargando perfil profesional…</p>;
  }
  if (isError) {
    console.error('[ProProfilePage] Error fetching professional:', error);
    return <p style={{ padding: 20 }}>Error al cargar el perfil profesional.</p>;
  }

  // Contenido final
  return (
    <div className="modalProfileCard" style={{ margin: '20px auto', maxWidth: 600 }}>
      {/* Imagen de cabecera */}
      {profData.cabecera && (
        <div className="modalProfHeaderImage">
          <img src={profData.cabecera} alt="Cabecera" />
        </div>
      )}

      {/* Info principal */}
      <div className="modalProfInfoTop">
        {profData.tipo && (
          <p className="icons-info">
            <IoPeopleOutline className="icon" />
            <span className="info-text">{profData.tipo}</span>
          </p>
        )}
        <div className="contact-info">
          {profData.telefono && (
            <div className="icons-info">
              <PiPhone className="icon" />
              <span className="info-text">
                <a href={`tel:${profData.telefono}`}>{profData.telefono}</a>
              </span>
            </div>
          )}
          {profData.whatsapp && (
            <div className="icons-info">
              <FaWhatsapp className="icon" />
              <span className="info-text">
                <a
                  href={`https://wa.me/34${profData.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {profData.whatsapp}
                </a>
              </span>
            </div>
          )}
        </div>
        {profData.email && (
          <p className="icons-info">
            <HiOutlineMail className="icon" />
            <span className="info-text">
              <a href={`mailto:${profData.email}`}>{profData.email}</a>
            </span>
          </p>
        )}
        {profData.horario && (
          <p className="icons-info">
            <TfiAlarmClock className="icon" />
            <span className="info-text">{profData.horario}</span>
          </p>
        )}
      </div>

      {/* Botones principales sticky */}
      <div
        ref={buttonContainerRef}
        className={`modalProfButtonContainer ${isStuck ? 'stuck' : ''}`}
      >
        <div className="redesSociales">
          {profData.enlace && (
            <div className="redesSociales-text">
              <a
                href={profData.enlace}
                target="_blank"
                rel="noopener noreferrer"
                className="web-link"
              >
                Visita nuestra web
              </a>
            </div>
          )}
          <div className="redesSociales-icons">
            {profData.facebook && (
              <a
                href={profData.facebook}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/Facebook.png"
                  alt="Facebook"
                  className="social-icon"
                />
              </a>
            )}
            {profData.instagram && (
              <a
                href={profData.instagram}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/Instagram.png"
                  alt="Instagram"
                  className="social-icon"
                />
              </a>
            )}
            {profData.twitter && (
              <a
                href={profData.twitter}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/Twitter.png"
                  alt="Twitter"
                  className="social-icon"
                />
              </a>
            )}
            {profData.youtube && (
              <a
                href={profData.youtube}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/Youtube.png"
                  alt="Youtube"
                  className="social-icon"
                />
              </a>
            )}
          </div>
        </div>
        <div className="prof-buttons-row">
          <ActionButton
            label="Acoge"
            url={profData.acoge}
            onClick={scrollToButtons}
          />
          <ActionButton
            label="Apadrina"
            url={profData.apadrina}
            onClick={scrollToButtons}
          />
          <ActionButton
            label="Voluntariado"
            url={profData.voluntariado}
            onClick={scrollToButtons}
          />
          <ActionButton
            label="Dona"
            url={profData.donaciones}
            onClick={scrollToButtons}
          />
          <ActionButton
            label="Socios"
            url={profData.socio}
            onClick={scrollToButtons}
          />
        </div>
      </div>

      {/* Descripción larga */}
      <div className="modalProfDetails">
        {profData.aboutus && <p>{profData.aboutus}</p>}
      </div>
    </div>
  );
}
